import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, CheckSquare, ShoppingCart, Package, 
  AlertTriangle, Clock, Truck, TrendingUp, 
  ArrowRight, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { dashboardAPI, branchAPI } from '@/lib/api';
import { cn, formatDate, getStatusColor, getUrgencyBorder } from '@/lib/utils';
import useAuthStore from '@/store/authStore';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const KPICard = ({ title, value, icon: Icon, color, loading, href }) => (
  <Card className={cn("border-l-4", color)}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          {loading ? (
            <Skeleton className="h-8 w-16 mt-1" />
          ) : (
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          )}
        </div>
        <div className={cn("p-3 rounded-lg", color.replace('border-l-', 'bg-').replace('-500', '-100'))}>
          <Icon className={cn("w-6 h-6", color.replace('border-l-', 'text-'))} />
        </div>
      </div>
      {href && (
        <Link 
          to={href} 
          className="mt-3 flex items-center text-sm text-slate-600 hover:text-slate-900"
        >
          View details <ArrowRight className="w-4 h-4 ml-1" />
        </Link>
      )}
    </CardContent>
  </Card>
);

const AgingWidget = ({ title, items, loading, type }) => (
  <Card className="h-full">
    <CardHeader className="pb-2">
      <CardTitle className="text-lg font-semibold flex items-center gap-2">
        <Clock className="w-5 h-5 text-amber-500" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-slate-500">
          <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>No items to display</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.slice(0, 5).map((item) => (
            <div 
              key={item.id} 
              className={cn(
                "p-3 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors",
                item.days_aged > 3 ? "bg-red-50 border-red-200" : "bg-white"
              )}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-sm font-medium text-slate-900 truncate">
                    {item.reference}
                  </p>
                  <p className="text-sm text-slate-600 truncate">{item.description}</p>
                </div>
                <Badge variant={item.days_aged > 3 ? "destructive" : "secondary"} className="ml-2 shrink-0">
                  {item.days_aged}d
                </Badge>
              </div>
              {item.branch && (
                <p className="text-xs text-slate-500 mt-1">{item.branch}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

const ActivityItem = ({ activity }) => {
  const iconMap = {
    create: FileText,
    approve: CheckSquare,
    reject: AlertTriangle,
    status_update: TrendingUp,
    login: null,
    register: null,
  };
  
  const Icon = iconMap[activity.type] || FileText;
  
  if (!Icon) return null;

  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
      <div className="p-2 rounded-lg bg-slate-100">
        <Icon className="w-4 h-4 text-slate-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-900">{activity.description}</p>
        <p className="text-xs text-slate-500">{formatDate(activity.timestamp)}</p>
      </div>
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useAuthStore();
  const [kpis, setKpis] = useState(null);
  const [agingRequests, setAgingRequests] = useState([]);
  const [agingPOs, setAgingPOs] = useState([]);
  const [agingPickups, setAgingPickups] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (branchId = null) => {
    try {
      const branchFilter = branchId && branchId !== 'all' ? branchId : null;
      
      const [kpisRes, requestsRes, posRes, pickupsRes, activityRes] = await Promise.all([
        dashboardAPI.getKPIs(branchFilter),
        dashboardAPI.getAging('requests', branchFilter),
        dashboardAPI.getAging('pos', branchFilter),
        dashboardAPI.getAging('pickups', branchFilter),
        dashboardAPI.getRecentActivity(15),
      ]);

      setKpis(kpisRes.data);
      setAgingRequests(requestsRes.data.items || []);
      setAgingPOs(posRes.data.items || []);
      setAgingPickups(pickupsRes.data.items || []);
      setRecentActivity(activityRes.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll();
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchData(selectedBranch);
    }
  }, [selectedBranch]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(selectedBranch);
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-600">Welcome back, {user?.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-48" data-testid="branch-filter">
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branch.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Open Requests"
          value={kpis?.total_open_requests || 0}
          icon={FileText}
          color="border-l-blue-500"
          loading={loading}
          href="/requests"
        />
        <KPICard
          title="Pending Approvals"
          value={kpis?.pending_approvals || 0}
          icon={CheckSquare}
          color="border-l-amber-500"
          loading={loading}
          href="/approvals"
        />
        <KPICard
          title="Active POs"
          value={kpis?.active_pos || 0}
          icon={ShoppingCart}
          color="border-l-indigo-500"
          loading={loading}
          href="/purchase-orders"
        />
        <KPICard
          title="Ready for Pickup"
          value={kpis?.ready_for_pickup || 0}
          icon={Package}
          color="border-l-emerald-500"
          loading={loading}
          href="/receiving"
        />
      </div>

      {/* Alert Cards */}
      {(kpis?.overdue_pos > 0 || kpis?.recent_exceptions > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {kpis?.overdue_pos > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-red-900">Overdue POs</p>
                  <p className="text-sm text-red-700">{kpis.overdue_pos} purchase orders past expected delivery</p>
                </div>
              </CardContent>
            </Card>
          )}
          {kpis?.recent_exceptions > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Truck className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">Recent Exceptions</p>
                  <p className="text-sm text-amber-700">{kpis.recent_exceptions} parts lost/damaged in last 7 days</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Aging Widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <AgingWidget
          title="Pending Requests"
          items={agingRequests}
          loading={loading}
          type="requests"
        />
        <AgingWidget
          title="Active POs"
          items={agingPOs}
          loading={loading}
          type="pos"
        />
        <AgingWidget
          title="Ready for Pickup"
          items={agingPickups}
          loading={loading}
          type="pickups"
        />
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : recentActivity.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              <p>No recent activity</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentActivity.filter(a => a.type !== 'login' && a.type !== 'register').slice(0, 10).map((activity) => (
                <ActivityItem key={activity.id} activity={activity} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
